import { emptyFragment } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import { observer } from 'mobx-react';
import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Main from '../../../BrowserSupport/Main';
import I18nTitle from '../../../Title/I18nTitle';
import { T } from '../../../Translations';
import TriangleExclamationIcon from '../../Cutesies/TriangleExclamationIcon';
import FlashMessage from '../../FlashMessage';
import MainNav from '../../Platform/MainNav';
import { createNewGroup, createSelectedGroup } from '../../Form/GroupInputWithSuggestions/Types';
import { trackIntercomEvent } from '../../../services/intercomTrackingService';
import GroupSelectionSection from '../Participants/AddParticipant/GroupSelectionSection';
import LicenseSelectionSection from '../Participants/AddParticipant/LicenseSelectionSection';
import { createSelectedAsAuditorLicense, createSelectedLicense } from './Store/Types';
import HelloCSVImporter from './HelloCSV';
import ParticipantChips from './ParticipantChips';
import { importParticipantsV2Store } from './Store';
import ImportParticipantsV2Reactions from './Store/Reactions';
import { importFormStore } from './Store/FormStore';
import SuccessReport from './SuccessReport';

function SpinnerIcon() {
  return (
    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ImportParticipantsV2() {
  const navigate = useNavigate();
  const store = importParticipantsV2Store;

  const handleClose = useCallback(() => {
    navigate('/people/participants');
  }, [navigate]);

  useEffect(() => {
    store.load();
    importFormStore.reset();
    return () => {
      store.reset();
      importFormStore.reset();
    };
  }, [store]);

  const allGroups = store.availableUserGroups;
  const allLicenses = store.availableLicenses;

  const handleGroupToggle = useCallback(
    (groupId: number, checked: boolean) => {
      const group = allGroups.find((g) => g.id === groupId);

      const newSelectedGroupIds = checked
        ? [...importFormStore.selectedGroupIds, groupId]
        : importFormStore.selectedGroupIds.filter((id: number) => id !== groupId);

      const allSuggestedLicenseIds = new Set<number>();
      newSelectedGroupIds.forEach((gId: number) => {
        const g = allGroups.find((gr) => gr.id === gId);
        if (g && g.associatedLicenseIds.length > 0) {
          g.associatedLicenseIds.forEach((licId) => allSuggestedLicenseIds.add(licId));
        }
      });

      if (checked && group) {
        const associatedIds = group.associatedLicenseIds;
        importFormStore.updateSelectedGroupIds(newSelectedGroupIds);
        importFormStore.updateGroupSuggestedLicenseIds(allSuggestedLicenseIds);
        if (associatedIds.length > 0) {
          importFormStore.updateSelectedLicenseIds([
            ...new Set([...importFormStore.selectedLicenseIds, ...associatedIds]),
          ]);
        }
      } else {
        const oldSuggestedLicenseIds = importFormStore.groupSuggestedLicenseIds;
        const newSelectedLicenseIds = importFormStore.selectedLicenseIds.filter((id) => {
          if (allSuggestedLicenseIds.has(id)) return true;
          if (!oldSuggestedLicenseIds.has(id)) return true;
          return false;
        });
        importFormStore.updateSelectedGroupIds(newSelectedGroupIds);
        importFormStore.updateGroupSuggestedLicenseIds(allSuggestedLicenseIds);
        importFormStore.updateSelectedLicenseIds(newSelectedLicenseIds);

        if (newSelectedLicenseIds.length === 0 && importFormStore.areLicensesAuditor) {
          importFormStore.updateAreLicensesAuditor(false);
        }
      }
    },
    [allGroups],
  );

  const handleLicenseToggle = useCallback((licenseId: number, checked: boolean) => {
    if (importFormStore.groupSuggestedLicenseIds.has(licenseId)) return;

    const newSelectedLicenseIds = checked
      ? [...importFormStore.selectedLicenseIds, licenseId]
      : importFormStore.selectedLicenseIds.filter((id) => id !== licenseId);

    importFormStore.updateSelectedLicenseIds(newSelectedLicenseIds);

    if (newSelectedLicenseIds.length === 0 && importFormStore.areLicensesAuditor) {
      importFormStore.updateAreLicensesAuditor(false);
    }
  }, []);

  const toggleLicenseTypeExpanded = (typeName: string) => {
    const next = new Set(importFormStore.expandedLicenseTypes);
    if (next.has(typeName)) {
      next.delete(typeName);
    } else {
      next.add(typeName);
    }
    importFormStore.updateExpandedLicenseTypes(next);
  };

  const handleCreateNewGroup = () => {
    if (!importFormStore.newGroupName.trim()) return;
    const groupName = importFormStore.newGroupName.trim();
    const newGroupId = -Date.now();
    const newMap = new Map(importFormStore.newGroupIdMap);
    newMap.set(newGroupId, groupName);
    importFormStore.updateNewGroupIdMap(newMap);
    importFormStore.updateSelectedGroupIds([...importFormStore.selectedGroupIds, newGroupId]);
    importFormStore.updateNewGroupName('');
    importFormStore.updateShowCreateGroupInput(false);
    importFormStore.updateGroupSearchQuery('');
  };

  const handleCreateFromSearch = (groupName: string) => {
    trackIntercomEvent('Import Participants - Created New Group', { groupName });
  };

  const handleConfirm = () => {
    // Map selectedGroupIds → SelectableUserGroup[]
    const selectedGroups = importFormStore.selectedGroupIds
      .map((id: number) => {
        if (id < 0) {
          const groupName = importFormStore.newGroupIdMap.get(id);
          if (!groupName) return null;
          return createNewGroup(groupName);
        }
        const group = allGroups.find((g) => g.id === id);
        if (!group) return null;
        return createSelectedGroup({
          id: group.id,
          name: group.name,
          organization: group.organization,
          memberCount: group.memberCount,
          assignedLicenseCount: group.assignedLicenseCount,
          assignedLicenseIds: group.assignedLicenseIds,
        });
      })
      .filter((g): g is NonNullable<typeof g> => g !== null);

    // Map selectedLicenseIds → SelectableLicense[]
    const createLicense = importFormStore.areLicensesAuditor
      ? createSelectedAsAuditorLicense
      : createSelectedLicense;
    const selectedLicenses = importFormStore.selectedLicenseIds
      .map((id: number) => {
        const license = allLicenses.find((l) => l.id === id);
        return license ? createLicense(license) : null;
      })
      .filter((l): l is NonNullable<typeof l> => l !== null);

    store.setSelectedGroups(selectedGroups);
    store.setSelectedLicenses(selectedLicenses);
    store.submit();
  };

  const isSubmitting = store.state.kind === 'submitting';
  const canSubmit = store.hasParticipantsToImport && !isSubmitting;

  const renderContent = () => {
    switch (store.state.kind) {
      case 'waiting':
      case 'loading-options':
        return (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
            <span className="ml-3 text-muted-foreground">
              <T kind="Loading..." />
            </span>
          </div>
        );

      case 'options-load-error':
      case 'error':
        return (
          <div className="text-center py-12">
            <div className="flex justify-center mb-4">
              <TriangleExclamationIcon width="48" height="48" />
            </div>
            <p className="text-muted-foreground">
              <T kind="Error loading import options. Please try again." />
            </p>
            <button
              onClick={() => {
                store.reset();
                store.load();
              }}
              className="mt-4 px-4 py-2 bg-accent text-accent-foreground rounded-md hover:opacity-90"
              data-intercom-target="import-participants-v2-retry"
            >
              <T kind="Retry" />
            </button>
          </div>
        );

      case 'options-ready':
      case 'file-processed':
      case 'submit-failed':
        return renderFormContent();

      case 'hellocsv-open':
        return (
          <>
            {renderFormContent()}
            <HelloCSVImporter onClose={() => store.closeHelloCsv()} />
          </>
        );

      case 'submitting':
        // Match AddParticipant: the Confirm button shows an inline spinner
        // while submitting; no blocking full-screen overlay.
        return renderFormContent();

      case 'submit-succeeded':
      case 'view-report':
        return (
          <>
            {renderFormContent()}
            <SuccessReport
              onClose={handleClose}
              onAddAnother={() => {
                store.prepareForNextImport();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </>
        );

      default:
        assertNever(store.state);
    }
  };

  const renderFormContent = () => (
    <div className="space-y-6">
      {/* Section 1: Upload CSV */}
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="flex items-start gap-4 mb-6">
          <div
            className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-lg"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            1
          </div>
          <div className="flex-1">
            <h2 className="text-xl mb-1" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
              <T kind="Confirm Participant(s)" />
            </h2>
            <p className="text-muted-foreground">
              <T kind="Import a CSV file including the names and emails for the participant(s) you are adding." />
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            const headers = ['email', 'firstname', 'lastname', 'login_type'];
            const sampleRows = [
              ['john.doe@example.com', 'John', 'Doe', ''],
              ['jane.smith@example.com', 'Jane', 'Smith', 'always_use_sso'],
              ['bob.wilson@example.com', 'Bob', 'Wilson', 'do_not_use_sso'],
            ];
            const csvContent =
              headers.join(',') + '\n' + sampleRows.map((row) => row.join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'import_participants_template.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }}
          className="text-primary hover:opacity-80 transition-opacity mb-6 block"
          style={{ fontWeight: 'var(--font-weight-medium)' }}
          data-intercom-target="import-participants-v2-download-template"
        >
          <T kind="Download Template" />
        </button>

        <ParticipantChips onUploadClick={() => store.openHelloCsv()} />
      </div>

      {/* Section 2: Groups */}
      <GroupSelectionSection
        formStore={importFormStore}
        groups={allGroups}
        licenses={allLicenses}
        handleGroupToggle={handleGroupToggle}
        handleCreateNewGroup={handleCreateNewGroup}
        onCreateFromSearch={handleCreateFromSearch}
      />

      {/* Section 3: Licenses */}
      <LicenseSelectionSection
        formStore={importFormStore}
        groups={allGroups}
        licenses={allLicenses}
        canAddAsAuditor={store.canAddAsAuditor}
        handleLicenseToggle={handleLicenseToggle}
        toggleLicenseTypeExpanded={toggleLicenseTypeExpanded}
      />
    </div>
  );

  return (
    <>
      <ImportParticipantsV2Reactions store={store} />
      <I18nTitle title="Import Participants" />
      <div className="min-h-screen flex flex-col">
        <MainNav />

        <div className="border-b border-border bg-background py-6">
          <div className="max-w-5xl mx-auto px-12">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  <T kind="Import Participants" />
                </h1>
                <p className="text-muted-foreground">
                  <T kind="Import participants, add them to a group and assign them a license." />
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-4 py-2 text-primary hover:opacity-80 transition-opacity"
                  style={{ fontWeight: 'var(--font-weight-medium)' }}
                  onClick={handleClose}
                  data-intercom-target="import-participants-v2-cancel"
                >
                  <T kind="Cancel" />
                </button>
                <button
                  className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontWeight: 'var(--font-weight-medium)' }}
                  disabled={!canSubmit}
                  onClick={handleConfirm}
                  data-intercom-target="import-participants-v2-confirm"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <SpinnerIcon />
                      <T kind="Importing participants..." />
                    </span>
                  ) : (
                    <T kind="Confirm" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-grow bg-muted/30">
          <div className="max-w-5xl mx-auto px-12 py-8">
            <Main id="main">
              {store.notification
                .map((alert) => <FlashMessage alert={alert} />)
                .getOrElse(emptyFragment)}
              {renderContent()}
            </Main>
          </div>
        </div>

        <div className="border-t border-border bg-card py-4">
          <div className="max-w-5xl mx-auto px-12">
            <div className="flex items-center justify-between">
              <button
                className="text-primary hover:opacity-80 transition-opacity"
                style={{ fontWeight: 'var(--font-weight-medium)' }}
                onClick={handleClose}
                data-intercom-target="import-participants-v2-cancel-bottom"
              >
                <T kind="Cancel" />
              </button>
              <button
                className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontWeight: 'var(--font-weight-medium)' }}
                disabled={!canSubmit}
                onClick={handleConfirm}
                data-intercom-target="import-participants-v2-confirm-bottom"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <SpinnerIcon />
                    <T kind="Importing participants..." />
                  </span>
                ) : (
                  <T kind="Confirm" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default observer(ImportParticipantsV2);
